/**
 * Route category matchers for authentication and navigation logic.
 * Centralizes route classification to avoid hardcoded paths throughout the app.
 */

// Authentication-related routes (login, signup, etc.)
export const AUTH_ROUTES = [
  /^\/login$/,
  /^\/signup$/,
  /^\/forgot-password$/,
  /^\/reset-password/,
  /^\/accept-invite$/
];

// Onboarding flow routes
export const ONBOARDING_ROUTES = [/^\/onboarding(\/|$)/];

// Public routes that don't require authentication
export const PUBLIC_ROUTES = [
  /^\/(legal|privacy|pricing)(\/|$)/,
  /^\/booking(\/|$)/,
  /^\/patient\/[^/]+$/,
  /^\/patient\/[^/]+\/form\/[^/]+$/,
  /^\/patient\/[^/]+\/reschedule$/,
  /^\/r\/[^/]+$/,
  /^\/periochart(\/|$)/,
  /^\/codes-catalog(\/|$)/,
  /^\/reviews(\/|$)/,
  /^\/analytics(\/|$)/,
  /^\/upload(\/|$)/,
];

/**
 * Check if a path matches any of the given route patterns
 */
export const isRoute = (path: string, patterns: RegExp[]): boolean =>
  patterns.some(regex => regex.test(path));

/**
 * Check if a redirect path is safe (internal and not malicious)
 * Prevents open redirect vulnerabilities
 */
export const isSafeInternalPath = (path?: string | null): boolean =>
  !!path && path.startsWith("/") && !path.startsWith("//");

/**
 * Route classification helpers
 */
export const routeHelpers = {
  isAuthPage: (path: string) => isRoute(path, AUTH_ROUTES),
  isOnboardingPage: (path: string) => isRoute(path, ONBOARDING_ROUTES),
  isPublicPage: (path: string) => isRoute(path, PUBLIC_ROUTES),
  // Reset password must remain accessible even if a (temporary) session exists
  isResetPasswordPage: (path: string) => /^\/reset-password(\/|$)?/.test(path),
  // Accept-invite must remain accessible for users without active practice
  isAcceptInvitePage: (path: string) => /^\/accept-invite$/.test(path),
  isProtectedPage: (path: string) => 
    !routeHelpers.isAuthPage(path) && 
    !routeHelpers.isOnboardingPage(path) && 
    !routeHelpers.isPublicPage(path),
};
